import { useEffect, useRef, useState, type ComponentType, type MouseEvent as ReactMouseEvent, type SVGProps } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  IconBed,
  IconBillCheck,
  IconChart,
  IconClock,
  IconDroplet,
  IconEdit,
  IconIdCard,
  IconOtLamp,
  IconPill,
  IconPrinter,
  IconPulse,
  IconReports,
  IconRefreshCw,
  IconStore,
  IconTrash,
  IconWallet,
} from '../../components/layout/icons';
import { BillActionGrid, BillActionTile } from '../../components/ui/BillActionTile';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { confirmAction } from '../../lib/confirm-service';
import { doctorNameOrDash } from '../../lib/doctor-name';
import { useToast } from '../../lib/toast';
import type { BillableAdmission } from './billable-admission';
import { IpdAllBillList } from './IpdAllBillList';
import { IpdAllReceiptList } from './IpdAllReceiptList';
import { IpdAllRefundList } from './IpdAllRefundList';
import { IpdBedTransferPanel } from './IpdBedTransferPanel';
import { IpdBillDetailsView } from './IpdBillDetailsView';
import { IpdCreateBillForm } from './IpdCreateBillForm';
import { IpdDischargeSummaryForm } from './IpdDischargeSummaryForm';
import { IpdEditAdmissionForm } from './IpdEditAdmissionForm';
import { IpdEditBillForm } from './IpdEditBillForm';
import { IpdNewReceiptForm } from './IpdNewReceiptForm';
import { IpdPrescriptionForm } from './IpdPrescriptionForm';
import { IpdRequisitionPanel } from './IpdRequisitionPanel';
import { useCancelIpdDaycareAdmission, useIpdBills, useIpdDaycareAdmission, useIpdDischargeSummary } from './ipd-daycare.queries';
import { currency, formatIpdDateTime } from './ipd-daycare.utils';

type DetailTab = 'admission-details' | 'bill-details' | 'create-bill' | 'edit-bill' | 'all-bill' | 'all-receipt' | 'all-refund' | 'new-receipt' | 'print-bill' | 'create-prescription' | 'edit-admission' | 'bed-transfer' | 'discharge-summary' | 'requisition';
type TileMenuKind = 'discharge' | 'adm-form';
type SectionNavigationTarget = { to: string; title: string; description: string; confirmLabel: string };
const cancelledHiddenTabs: DetailTab[] = ['create-bill', 'edit-bill', 'new-receipt', 'create-prescription', 'edit-admission', 'bed-transfer', 'discharge-summary', 'requisition'];

const blankTabs: Array<{ key: Exclude<DetailTab, 'admission-details' | 'bill-details' | 'create-bill' | 'edit-bill' | 'all-bill' | 'all-receipt' | 'all-refund' | 'new-receipt' | 'create-prescription' | 'edit-admission' | 'bed-transfer' | 'discharge-summary'>; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; soon: boolean; message: string }> = [
  { key: 'print-bill', label: 'Print Bill', icon: IconPrinter, soon: false, message: 'No bill has been created for this admission yet. Use Create New Bill first.' },
];

const billTiles: Array<{ key: 'bill-details' | 'create-bill' | 'edit-bill' | 'all-bill' | 'all-receipt' | 'all-refund' | 'new-receipt'; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }> = [
  { key: 'bill-details', label: 'Bill Details', icon: IconWallet },
  { key: 'create-bill', label: 'Create New Bill', icon: IconPulse },
  { key: 'edit-bill', label: 'Edit Bill', icon: IconEdit },
  { key: 'new-receipt', label: 'New Receipt', icon: IconClock },
  { key: 'all-bill', label: 'All Bill', icon: IconChart },
  { key: 'all-receipt', label: 'All Receipt', icon: IconChart },
  { key: 'all-refund', label: 'All Refund', icon: IconRefreshCw },
];

const upperText = (value: string | number | null | undefined, fallback = '-') => {
  const text = typeof value === 'string' ? value.trim() : value == null ? '' : String(value);
  return text ? text.toUpperCase() : fallback;
};

export const IpdDaycareDetailPage = () => {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTab = params.get('tab');
  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab === 'bill-details' ? 'bill-details' : 'admission-details');
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [tileMenu, setTileMenu] = useState<{ kind: TileMenuKind; top: number; left: number } | null>(null);
  const [sectionNavigation, setSectionNavigation] = useState<SectionNavigationTarget | null>(null);
  const { showToast } = useToast();
  const tileMenuRef = useRef<HTMLDivElement>(null);
  const query = useIpdDaycareAdmission(admissionId);
  const billsQuery = useIpdBills(admissionId);
  const dischargeQuery = useIpdDischargeSummary(admissionId);
  const cancelAdmission = useCancelIpdDaycareAdmission();
  useEffect(() => { if (query.data && params.get('print') === '1') window.setTimeout(() => window.print(), 250); }, [params, query.data]);
  useEffect(() => {
    const hasAnyBill = Boolean(query.data?.billing?.hasBill) || Boolean(billsQuery.data?.length);
    const canResolveNoBill = Boolean(query.data) && (Boolean(query.data?.billing?.hasBill) || billsQuery.isFetched);
    if (canResolveNoBill && !hasAnyBill && (activeTab === 'bill-details' || activeTab === 'edit-bill')) {
      setActiveTab('admission-details');
    }
  }, [activeTab, billsQuery.data, billsQuery.isFetched, query.data]);
  useEffect(() => {
    if (query.data?.status === 'cancelled' && cancelledHiddenTabs.includes(activeTab)) setActiveTab('admission-details');
  }, [activeTab, query.data?.status]);
  useEffect(() => {
    if (!tileMenu) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!tileMenuRef.current?.contains(event.target as Node)) setTileMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setTileMenu(null); };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [tileMenu]);
  if (query.isLoading) return <DetailSkeleton label="Loading admission details" />;
  if (query.isError || !query.data) return <ErrorState title="IPD/Daycare admission not found" message="The admission could not be loaded." />;
  const row = query.data;
  const address = [row.patientSnapshot.address, row.patientSnapshot.district, row.patientSnapshot.state, row.patientSnapshot.pinCode].filter(Boolean).join(', ') || '-';
  const statusLabel = row.status === 'admitted' ? 'Admitted' : row.status === 'cancelled' ? 'Cancelled' : 'Discharged';
  const isCancelled = row.status === 'cancelled';
  const activeBlankTab = blankTabs.find((tab) => tab.key === activeTab);
  const bills = billsQuery.data ?? [];
  const hasAnyBill = Boolean(row.billing?.hasBill) || bills.length > 0;
  const latestBillId = bills[0]?.id ?? null;
  const effectiveBillId = selectedBillId ?? latestBillId;
  const discharge = dischargeQuery.data ?? null;
  const activeBills = bills.filter((bill) => bill.status !== 'cancelled');
  const totalDue = activeBills.reduce((sum, bill) => sum + bill.dueAmount, 0);
  const totalCredit = activeBills.reduce((sum, bill) => sum + bill.creditAmount, 0);
  const lastBillNumber = bills[0]?.billNumber ?? null;
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) void navigate(-1);
    else void navigate('/ipd-daycare');
  };
  const billableAdmission: BillableAdmission = {
    id: row.id,
    patient: row.patient,
    patientSnapshot: { name: row.patientSnapshot.name, uhid: row.patientSnapshot.uhid },
    bedCategory: row.bedCategory,
    bed: row.bed,
    admissionDate: row.admissionDate,
    status: row.status,
    admissionLabel: `IPD No ${row.ipdNumber}`,
  };

  const goToTab = (tab: DetailTab) => setActiveTab(tab);
  const viewBill = (billId: string) => { setSelectedBillId(billId); setActiveTab('bill-details'); };
  const confirmSectionNavigation = () => {
    const target = sectionNavigation;
    setSectionNavigation(null);
    if (target) void navigate(target.to);
  };
  const openTileMenu = (kind: TileMenuKind) => (event: ReactMouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTileMenu((current) => current?.kind === kind ? null : { kind, top: rect.bottom + 6, left: rect.left });
  };
  const openDischargePrint = (withHeader: boolean) => {
    setTileMenu(null);
    openAuthenticatedRoute(`/ipd-daycare/${row.id}/print-discharge?header=${withHeader ? '1' : '0'}`);
  };
  const openAdmissionFormPrint = (withHeader: boolean) => {
    setTileMenu(null);
    openAuthenticatedRoute(`/ipd-daycare/${row.id}/print-admission-form?header=${withHeader ? '1' : '0'}`);
  };
  const printBill = () => {
    if (!effectiveBillId) { setActiveTab('bill-details'); return; }
    openAuthenticatedRoute(`/ipd-daycare/${row.id}/print-bill/${effectiveBillId}?header=1`);
  };
  const cancelAdmissionIfClear = async () => {
    if (cancelAdmission.isPending || billsQuery.isRefetching) return;
    if (row.status !== 'admitted') {
      showToast('Only admitted patients can be cancelled.', 'warning');
      return;
    }

    try {
      const latestBills = (await billsQuery.refetch()).data ?? [];
      const activeBill = latestBills.find((bill) => bill.status !== 'cancelled');
      if (activeBill) {
        showToast(`Cancel bill #${activeBill.billNumber} before cancelling this admission.`, 'warning');
        setSelectedBillId(activeBill.id);
        setActiveTab('edit-bill');
        return;
      }
      const pendingRefundBill = latestBills.find((bill) => bill.creditAmount > 0);
      if (pendingRefundBill) {
        showToast(`Complete refund for bill #${pendingRefundBill.billNumber} before cancelling this admission.`, 'warning');
        setSelectedBillId(pendingRefundBill.id);
        setActiveTab('bill-details');
        return;
      }

      const confirmed = await confirmAction({
        title: 'Cancel Admission?',
        description: `Cancel IPD/Daycare admission #${row.ipdNumber} for ${row.patientSnapshot.name}? This will release the bed after bill cancellation and refunds are complete.`,
        confirmLabel: 'Yes, Cancel Admission',
        tone: 'danger',
      });
      if (!confirmed) return;

      cancelAdmission.mutate(row.id, {
        onSuccess: () => {
          showToast('Admission cancelled successfully.', 'success');
          setActiveTab('admission-details');
        },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The admission could not be cancelled.', 'error'),
      });
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : 'Unable to check bills before cancelling admission.', 'error');
    }
  };

  return (
    <div className="page-stack opd-page ipd-daycare-page opd-bill-page">
      <div className="page-heading opd-print-hidden">
        <div><p className="eyebrow">IPD / Daycare</p><h1>IPD / Daycare Admission Details</h1></div>
        <div className="opd-heading-actions">
          <button className="button button-secondary" type="button" onClick={goBack}>Back</button>
        </div>
      </div>

      <div className="opd-bill-layout">
        <aside className="content-card opd-bill-sidebar">
          <div className={`opd-bill-patient-box ipd-admission-status-watermark ${row.status}`} data-status={statusLabel}>
            <p><strong>Patient Name : </strong>{upperText(row.patientSnapshot.name)}</p>
            <p><strong>UHID : </strong>{upperText(row.patientSnapshot.uhid)}</p>
            <p><strong>Sex : </strong>{upperText(row.patientSnapshot.gender)} || <strong>Age : </strong>{row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</p>
            <p><strong>Mobile : </strong>{upperText(row.patientSnapshot.phone)}</p>
            <p><strong>Address : </strong>{upperText(address)}</p>
            <p><strong>Under Doctor : </strong>{upperText(doctorNameOrDash(row.underDoctor?.name))}</p>
            <p><strong>Department : </strong>{upperText(row.underDoctor?.department)}</p>
            <p><strong>Bed Details : </strong>{upperText(`${row.bedCategory.name} - ${row.bed.name}`)}</p>
            <p><strong>Admission Type : </strong>{upperText(row.admissionType)}</p>
            <p><strong>Admission Date : </strong>{upperText(formatIpdDateTime(row.admissionDate))}</p>
            {row.status === 'discharged' && discharge ? (
              <>
                <p><strong>Discharge Date : </strong>{upperText(formatIpdDateTime(discharge.dischargeDate))}</p>
                <p><strong>Discharge Type : </strong>{upperText(discharge.dischargeType)}</p>
              </>
            ) : null}
            <p><strong>Last Bill No : </strong>{upperText(lastBillNumber)}</p>
          </div>
          {totalCredit > 0 || totalDue > 0 ? (
            <div className="opd-bill-patient-balance">
              {totalCredit > 0 ? <p><strong>Credit Amount : </strong><span className="opd-bill-patient-credit">{currency(totalCredit)}</span></p> : null}
              {totalDue > 0 ? <p><strong>Overall Due : </strong><span className="opd-bill-patient-due">{currency(totalDue)}</span></p> : null}
            </div>
          ) : null}
          <BillActionGrid>
            <BillActionTile icon={IconIdCard} label="Patient Profile" to={`/patients/${row.patient.id}`} />
            {!isCancelled ? <BillActionTile icon={IconPill} label="Create E-Prescription" active={activeTab === 'create-prescription'} onClick={() => goToTab('create-prescription')} /> : null}
            <BillActionTile icon={IconBillCheck} label="Admission Details" active={activeTab === 'admission-details'} onClick={() => setActiveTab('admission-details')} />
            {!isCancelled ? <BillActionTile icon={IconEdit} label="Edit Admission" active={activeTab === 'edit-admission'} onClick={() => goToTab('edit-admission')} /> : null}
            {row.status === 'admitted' ? <BillActionTile icon={IconTrash} label={cancelAdmission.isPending || billsQuery.isRefetching ? 'Cancelling...' : 'Cancel Admission'} title="Cancel this admission after bill cancellation and refund completion" onClick={() => void cancelAdmissionIfClear()} /> : null}
            <BillActionTile icon={IconPrinter} label="Print Patient Labels" onClick={() => openAuthenticatedRoute(`/ipd-daycare/${row.id}/print-labels`)} />
            <BillActionTile icon={IconPrinter} label="Adm Form" active={tileMenu?.kind === 'adm-form'} onClick={openTileMenu('adm-form')} />
            {billTiles
              .filter((tab) => hasAnyBill || (tab.key !== 'bill-details' && tab.key !== 'edit-bill'))
              .filter((tab) => !isCancelled || !['create-bill', 'edit-bill', 'new-receipt'].includes(tab.key))
              .map((tab) => (
              <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} onClick={() => goToTab(tab.key)} />
            ))}
            {!isCancelled ? <BillActionTile icon={IconBed} label="Bed Transfer" active={activeTab === 'bed-transfer'} onClick={() => goToTab('bed-transfer')} /> : null}
            {!isCancelled ? <BillActionTile icon={IconReports} label="Discharge Summary" active={activeTab === 'discharge-summary'} onClick={() => goToTab('discharge-summary')} /> : null}
            {discharge ? <BillActionTile icon={IconPrinter} label="Print Discharge" active={tileMenu?.kind === 'discharge'} onClick={openTileMenu('discharge')} /> : null}
            {!isCancelled ? <BillActionTile icon={IconStore} label="Requisition" active={activeTab === 'requisition'} onClick={() => goToTab('requisition')} /> : null}
            {!isCancelled ? (
              <>
                <BillActionTile
                  icon={IconDroplet}
                  label="Move To Dialysis"
                  title="Register this patient for a Dialysis admission in the Dialysis module"
                  onClick={() => setSectionNavigation({
                    to: `/dialysis/register?from-admission=${row.id}`,
                    title: 'Move To Dialysis?',
                    description: `This will open Dialysis Admission for ${upperText(row.patientSnapshot.name)} using this IPD admission details.`,
                    confirmLabel: 'Move To Dialysis',
                  })}
                />
                <BillActionTile
                  icon={IconOtLamp}
                  label="OT Registration"
                  title="Register this patient for an OT case in the OT module"
                  onClick={() => setSectionNavigation({
                    to: `/ot/register?from-admission=${row.id}`,
                    title: 'Go To OT Registration?',
                    description: `This will open OT Registration for ${upperText(row.patientSnapshot.name)} using this IPD admission details.`,
                    confirmLabel: 'OT Registration',
                  })}
                />
              </>
            ) : null}
            {blankTabs.map((tab) => (
              tab.key === 'print-bill'
                ? <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} onClick={printBill} />
                : <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
            ))}
          </BillActionGrid>
        </aside>

        <section className="content-card opd-bill">
          {activeTab === 'bill-details' ? (
            <IpdBillDetailsView admission={billableAdmission} selectedBillId={effectiveBillId} onCreateBill={() => goToTab('create-bill')} />
          ) : activeTab === 'create-prescription' ? (
            <IpdPrescriptionForm row={row} />
          ) : activeTab === 'create-bill' ? (
            <IpdCreateBillForm admission={row} onCreated={(billId) => { setSelectedBillId(billId); goToTab('bill-details'); }} />
          ) : activeTab === 'edit-bill' ? (
            effectiveBillId ? (
              <IpdEditBillForm admission={row} billId={effectiveBillId} onCancelled={() => goToTab('bill-details')} />
            ) : (
              <EmptyState title="No bills yet" message="Create a bill for this admission before editing one." action={{ label: 'Create New Bill', onClick: () => goToTab('create-bill') }} />
            )
          ) : activeTab === 'all-bill' ? (
            <IpdAllBillList admission={billableAdmission} onViewBill={viewBill} />
          ) : activeTab === 'all-receipt' ? (
            <IpdAllReceiptList admission={billableAdmission} />
          ) : activeTab === 'all-refund' ? (
            <IpdAllRefundList admission={billableAdmission} />
          ) : activeTab === 'new-receipt' ? (
            <IpdNewReceiptForm admission={billableAdmission} onRecorded={(billId) => viewBill(billId)} />
          ) : activeTab === 'edit-admission' ? (
            <IpdEditAdmissionForm admission={row} onSaved={() => goToTab('admission-details')} />
          ) : activeTab === 'bed-transfer' ? (
            <IpdBedTransferPanel admission={row} />
          ) : activeTab === 'discharge-summary' ? (
            <IpdDischargeSummaryForm admission={row} />
          ) : activeTab === 'requisition' ? (
            <IpdRequisitionPanel admission={row} />
          ) : activeBlankTab ? (
            <EmptyState
              title={`${activeBlankTab.label}${activeBlankTab.soon ? ' — Coming soon' : ''}`}
              message={activeBlankTab.message}
              action={{ label: 'Back to Admission Details', onClick: () => setActiveTab('admission-details') }}
            />
          ) : (
          <>
          <header className="opd-bill-banner">
            <div className="opd-bill-banner-row">
              <div className="opd-bill-banner-fields">
                <span>IPD No : {row.ipdNumber}</span>
                <span>Admission Date : {formatIpdDateTime(row.admissionDate)}</span>
                <span>Admission Type : {row.admissionType}</span>
              </div>
              <span className={`opd-badge ${row.status === 'admitted' ? 'active' : 'inactive'}`}>{statusLabel}</span>
            </div>
            <p>Registered By - {row.generatedBy.name}</p>
          </header>

          <section className="opd-bill-subsection">
            <h3>Patient Details</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Patient</dt><dd>{row.patientSnapshot.name}</dd></div>
              <div><dt>UHID</dt><dd>{row.patientSnapshot.uhid ?? '-'}</dd></div>
              <div><dt>Mobile</dt><dd>{row.patientSnapshot.phone ?? '-'}</dd></div>
              <div><dt>Gender / Age</dt><dd>{row.patientSnapshot.gender}, {row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</dd></div>
              <div><dt>Marital Status</dt><dd>{row.patientSnapshot.maritalStatus ?? '-'}</dd></div>
              <div><dt>Guardian Name</dt><dd>{row.patientSnapshot.guardianName ?? '-'}</dd></div>
              <div><dt>Relation</dt><dd>{row.patientSnapshot.relation ?? '-'}</dd></div>
              <div><dt>Alternative Number</dt><dd>{row.patientSnapshot.alternativeNumber ?? '-'}</dd></div>
              <div><dt>Aadhaar Card</dt><dd>{row.patientSnapshot.aadhaar ?? '-'}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{address}</dd></div>
            </dl>
          </section>

          <section className="opd-bill-subsection">
            <h3>Admission Information</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Admission Type</dt><dd>{row.admissionType}</dd></div>
              <div><dt>Admission Date</dt><dd>{formatIpdDateTime(row.admissionDate)}</dd></div>
              <div><dt>Package Type</dt><dd>{row.packageType}</dd></div>
              <div><dt>Insurance Type</dt><dd>{row.insuranceType}</dd></div>
              <div><dt>Insurance No.</dt><dd>{row.insuranceNumber ?? '-'}</dd></div>
              <div><dt>Under Doctor</dt><dd>{doctorNameOrDash(row.underDoctor?.name)}</dd></div>
              <div><dt>Other Under Doctor</dt><dd>{doctorNameOrDash(row.otherUnderDoctor?.name)}</dd></div>
              <div><dt>Department</dt><dd>{row.underDoctor?.department ?? '-'}</dd></div>
              <div><dt>Bed Category</dt><dd>{row.bedCategory.name}</dd></div>
              <div><dt>Bed</dt><dd>{row.bed.name}</dd></div>
              <div><dt>Referred By</dt><dd>{row.referredBy.name}</dd></div>
              <div><dt>Provider</dt><dd>{row.provider?.name ?? '-'}</dd></div>
              <div><dt>Market By</dt><dd>{row.marketBy?.name ?? '-'}</dd></div>
              <div className="wide"><dt>Symptoms</dt><dd dangerouslySetInnerHTML={{ __html: row.symptoms || '-' }} /></div>
              <div className="wide"><dt>Diagnosis at the time of Admission</dt><dd dangerouslySetInnerHTML={{ __html: row.diagnosisAtAdmission || '-' }} /></div>
              <div className="wide"><dt>History of alcoholism, tobacco or substance abuse</dt><dd dangerouslySetInnerHTML={{ __html: row.alcoholTobaccoHistory || '-' }} /></div>
              <div className="wide"><dt>Significant Past Medical and Surgical History</dt><dd dangerouslySetInnerHTML={{ __html: row.pastMedicalSurgicalHistory || '-' }} /></div>
              <div className="wide"><dt>Family History</dt><dd dangerouslySetInnerHTML={{ __html: row.familyHistory || '-' }} /></div>
            </dl>
          </section>

          <section className="opd-bill-subsection">
            <h3>Responsible Person Details</h3>
            <dl className="opd-bill-detail-grid">
              <div><dt>Name</dt><dd>{row.responsiblePerson.name ?? '-'}</dd></div>
              <div><dt>Relation With Patient</dt><dd>{row.responsiblePerson.relation ?? '-'}</dd></div>
              <div><dt>Phone No</dt><dd>{row.responsiblePerson.phone ?? '-'}</dd></div>
              <div><dt>Age</dt><dd>{row.responsiblePerson.age ?? '-'}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{row.responsiblePerson.address ?? '-'}</dd></div>
            </dl>
          </section>
          </>
          )}
        </section>
      </div>

      {tileMenu ? (
        <div className="opd-bill-tile-dropdown" ref={tileMenuRef} style={{ top: tileMenu.top, left: tileMenu.left }} role="menu">
          {tileMenu.kind === 'discharge' ? (
            <>
              <button type="button" role="menuitem" onClick={() => openDischargePrint(false)}><IconPrinter />Discharge Summary without Header</button>
              <button type="button" role="menuitem" onClick={() => openDischargePrint(true)}><IconPrinter />Discharge Summary with Header</button>
            </>
          ) : (
            <>
              <button type="button" role="menuitem" onClick={() => openAdmissionFormPrint(false)}><IconPrinter />Form without Header</button>
              <button type="button" role="menuitem" onClick={() => openAdmissionFormPrint(true)}><IconPrinter />Form with Header</button>
            </>
          )}
        </div>
      ) : null}
      <ConfirmDialog
        open={sectionNavigation !== null}
        title={sectionNavigation?.title ?? 'Continue?'}
        description={sectionNavigation?.description ?? ''}
        confirmLabel={sectionNavigation?.confirmLabel ?? 'Continue'}
        onCancel={() => setSectionNavigation(null)}
        onConfirm={confirmSectionNavigation}
      />
    </div>
  );
};
